<template>
  <Spinner
    v-if="isLoading"
    color="primary"
    size="5rem"
    width="0.5rem"
    class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
  />

  <div v-else>
    <v-row dense class="gap-4">
      <v-col cols="12">
        <v-text-field
          v-model="local.name"
          label="Nome da escala"
          data-testid="schedule-name-input"
          variant="outlined"
          color="primary"
          hide-details
        />
      </v-col>

      <v-row dense>
        <v-col cols="12" md="8">
          <v-autocomplete
            v-model="local.timezone"
            label="Fuso Horário"
            data-testid="schedule-timezone-input"
            :items="timezones"
            variant="outlined"
            color="primary20"
            hide-details
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :title="item.raw?.title" :subtitle="item.raw?.subtitle" />
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <DatePicker v-model="local.endDate" locale="pt-BR" :masks="{ input: `DD/MM/YYYY` }">
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Fim da escala"
                data-testid="schedule-end-date-input"
                :append-inner-icon="mdiCalendarBlankOutline"
                :model-value="inputValue"
                v-on="inputEvents"
                color="primary20"
                variant="outlined"
                hide-details
              />
            </template>
          </DatePicker>
        </v-col>
      </v-row>

      <v-col cols="12">
        <OperationalGroupSelect
          :model-value="local.operationalGroup"
          @update:model-value="
            (newValue) => {
              local.operationalGroup = newValue
            }
          "
          :items="operationalGroupOptions"
          data-testid="schedule-operational-group-input"
        />
      </v-col>

      <div class="flex flex-col gap-6 text-left">
        <div class="text-gray-600">
          <strong class="text-black">Unidade:</strong> {{ local.healthUnit.name }}
        </div>
      </div>
    </v-row>

    <!-- Tipo de agenda (Demanda, SPEC 2.2 §5.5; mockup D: antes da proporcionalidade): só com a flag. -->
    <AgendaTypeField
      v-if="flags.enabledDashboardDemand"
      v-model="local.agendaType"
      mode="edit"
      :readonly="agendaTypeReadonly"
      :detection="agendaDetection"
    />

    <p class="font-bold text-base text-[#323232] text-left mb-4 mt-4">
      Regras de proporcionalidade
    </p>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      <SelectableCard
        v-for="opt in proportionalityRuleOptions"
        :key="opt.value"
        :title="opt.title"
        :description="opt.description"
        :selected="local.proportionalityRule === opt.value"
        :data-testid="`proportionality-rule-${opt.value.toLowerCase().replace(/_/g, '-')}-card`"
        @click="local.proportionalityRule = opt.value"
      />
    </div>

    <p class="font-bold text-base text-[#323232] text-left mb-2 mt-4">
      Lembrete de confirmação pelo aplicativo
    </p>
    <div class="flex gap-2">
      <v-checkbox
        v-model="local.teamDoctorReminderEnabled"
        data-testid="team-doctor-reminder-checkbox"
        label="Lembrete para médico interno"
        density="compact"
        color="primary"
        hide-details="auto"
        :disabled="true"
        class="w-1/2"
      />
      <v-checkbox
        v-model="local.externalDoctorReminderEnabled"
        data-testid="external-doctor-reminder-checkbox"
        label="Lembrete para médico externo"
        density="compact"
        color="primary"
        hide-details="auto"
        class="w-1/2"
      />
    </div>

    <UnidentifiedCoverageScheduleCard
      v-if="coverageSupported"
      v-model="local.allowUnregisteredDoctor"
      class="mt-6"
    />
    <DisableUnidentifiedCoverageModal
      :show-modal="disableModal.open"
      :future-count="disableModal.future"
      :past-count="disableModal.past"
      @confirm="(releaseFuture) => settleDisable({ confirmed: true, releaseFuture })"
      @cancel="settleDisable({ confirmed: false, releaseFuture: false })"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, computed } from 'vue'
import { timezones } from '@/hooks/useCommon'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiCalendarBlankOutline } from '@mdi/js'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'

import { ScheduleResponse, OperationalGroup } from '@/Network/Types/Responses/ScheduleUnitResponses'
import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import Spinner from '@/components/others/Spinner.vue'
import { proportionalityRuleOptions } from '@/utils/schedule'
import OperationalGroupSelect from '@/components/designSystem/Select/OperationalGroupSelect.vue'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import UnidentifiedCoverageScheduleCard from './UnidentifiedCoverageScheduleCard.vue'
import DisableUnidentifiedCoverageModal from './DisableUnidentifiedCoverageModal.vue'
import { scheduleSupportsCoverage } from '@/utils/coverage'
import AgendaTypeField from '@/components/ManageScale/Schedule/AgendaTypeField.vue'
import type {
  AgendaDetectionResponse,
  ScheduleAgendaTypeValue
} from '@/Network/Types/Responses/DemandResponses'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

const props = defineProps<{ scheduleId: number }>()
const emit = defineEmits<{
  (e: 'update:isSaving', value: boolean): void
  (e: 'update:modelValue', val: any): void
}>()

const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const schedule = ref<ScheduleResponse | null>(null)
const originalSnapshot = ref('')
// Ocupado sem identificação (M8-B): só aparece quando o BE já expõe o campo.
const coverageSupported = ref(false)
const originalAllowUnregistered = ref(false)
const disableModal = reactive({ open: false, future: 0, past: 0 })
type DisableDecision = { confirmed: boolean; releaseFuture: boolean }
let resolveDisableDecision: ((decision: DisableDecision) => void) | null = null

async function askDisableConfirmation(): Promise<DisableDecision> {
  let counts: { future: number; past: number } | null = null
  try {
    counts = await Network.schedule.getUnregisteredCoverageCount(local.id)
  } catch (err) {
    notifyError(err, 'Não foi possível contar os plantões ocupados sem identificação.')
    return { confirmed: false, releaseFuture: false }
  }
  // Sem plantões marcados: salva direto, sem confirmação.
  if (!counts || counts.future + counts.past === 0) return { confirmed: true, releaseFuture: false }
  disableModal.future = counts.future
  disableModal.past = counts.past
  disableModal.open = true
  return new Promise((resolve) => {
    resolveDisableDecision = resolve
  })
}

function settleDisable(decision: DisableDecision) {
  disableModal.open = false
  // Cancelou o desligar: o switch volta ao original (ligado), sem alteração pendente.
  if (!decision.confirmed) local.allowUnregisteredDoctor = originalAllowUnregistered.value
  resolveDisableDecision?.(decision)
  resolveDisableDecision = null
}

const { flags } = useFeatureFlags()
const agendaDetection = ref<AgendaDetectionResponse | null>(null)

/**
 * Alterar o tipo de agenda exige `schedule_editing` na escala (Q18). A store de
 * permissões só é consultada com a flag ligada — flag off não muda nada.
 */
const agendaTypeReadonly = computed(() => {
  if (!flags.enabledDashboardDemand) return false
  return !usePermissionsStore().check({
    permission: AuthorizationPermissions.ScheduleEditing,
    resource: { objectType: AuthorizationResourcesAndSubjects.Schedule, objectId: props.scheduleId }
  })
})

function getLocalSnapshot() {
  return JSON.stringify({
    ...(flags.enabledDashboardDemand && { agendaType: local.agendaType }),
    name: local.name,
    timezone: local.timezone,
    endDate: local.endDate,
    proportionalityRule: local.proportionalityRule,
    operationalGroup: local.operationalGroup?.name ?? null,
    teamDoctorReminderEnabled: local.teamDoctorReminderEnabled,
    externalDoctorReminderEnabled: local.externalDoctorReminderEnabled,
    allowUnregisteredDoctor: local.allowUnregisteredDoctor
  })
}

const hasUnsavedChanges = computed(() => {
  if (!originalSnapshot.value) return false
  return getLocalSnapshot() !== originalSnapshot.value
})

const local = reactive({
  id: 0,
  name: '',
  timezone: '',
  endDate: null as Date | string | null,
  healthUnit: { id: 0, name: '' },
  proportionalityRule: ProportionalityRuleEnum.PROPORTIONAL_WITH_LIMIT,
  positions: [],
  requiresDoctorPaymentFormation: false,
  operationalGroup: null as OperationalGroup | null,
  teamDoctorReminderEnabled: true,
  externalDoctorReminderEnabled: true,
  allowUnregisteredDoctor: false,
  agendaType: 'AUTO' as ScheduleAgendaTypeValue
})

const allOperationalGroups = ref<OperationalGroup[]>([])
const fieldOptionsService = new FieldOptionsService()

const operationalGroupOptions = computed(() => {
  return allOperationalGroups.value
})

const load = async () => {
  isLoading.value = true
  try {
    const [scheduleData, allGroupsData] = await Promise.all([
      Network.schedule.getById(props.scheduleId) as Promise<ScheduleResponse>,
      fieldOptionsService.getOperationalGroups()
    ])

    schedule.value = scheduleData
    allOperationalGroups.value = allGroupsData
    Object.assign(local, scheduleData)
    coverageSupported.value = scheduleSupportsCoverage(scheduleData)
    originalAllowUnregistered.value = scheduleData.allowUnregisteredDoctor === true
    local.allowUnregisteredDoctor = originalAllowUnregistered.value
    local.agendaType = scheduleData.agendaType ?? 'AUTO'

    if (scheduleData.operationalGroup) {
      local.operationalGroup =
        allGroupsData.find((g) => g.name === scheduleData.operationalGroup) ?? null
    }

    originalSnapshot.value = getLocalSnapshot()
  } catch (err) {
    notifyError(err, 'Não foi possível carregar os dados da escala.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Detecção do tipo de agenda (só com a flag). Falha não bloqueia a aba: o campo
 * cai no texto "será detectado após 8 semanas".
 */
const loadAgendaDetection = async () => {
  if (!flags.enabledDashboardDemand) return
  try {
    agendaDetection.value = await Network.demand.agendaDetection(props.scheduleId)
  } catch (err) {
    console.error('Falha ao carregar a detecção do tipo de agenda', err)
    agendaDetection.value = null
  }
}

onMounted(() => {
  load()
  loadAgendaDetection()
})

watch(
  () => local,
  () => {
    emit('update:modelValue', { ...local })
  },
  { deep: true }
)

function validateBasicInfo(): boolean {
  if (!local.name.trim()) {
    toast.error('O nome da escala não pode ficar vazio.')
    return false
  }

  if (!local.timezone) {
    toast.error('Selecione um fuso horário.')
    return false
  }

  if (!local.proportionalityRule) {
    toast.error('Selecione uma regra de proporcionalidade.')
    return false
  }

  if (!local.operationalGroup) {
    toast.error('Selecione um Grupo Operacional.')
    return false
  }

  return true
}

// Desligar com plantões marcados pede confirmação (Q30-A); ligar ou manter salva direto.
async function resolveCoverageDecision(): Promise<DisableDecision> {
  const turningOff =
    coverageSupported.value && originalAllowUnregistered.value && !local.allowUnregisteredDoctor
  if (!turningOff) return { confirmed: true, releaseFuture: false }
  return askDisableConfirmation()
}

const handleSave = async () => {
  if (!validateBasicInfo()) return

  const decision = await resolveCoverageDecision()
  if (!decision.confirmed) return
  // Atalho desmarcado não viaja: só `true` é enviado.
  const releaseFutureUnregistered = decision.releaseFuture || undefined

  emit('update:isSaving', true)
  try {
    await Network.schedule.update(local.id, {
      name: local.name,
      timezone: local.timezone,
      endDate: local.endDate,
      proportionalityRule: local.proportionalityRule,
      operationalGroup: local.operationalGroup?.name ?? null,
      teamDoctorReminderEnabled: local.teamDoctorReminderEnabled,
      externalDoctorReminderEnabled: local.externalDoctorReminderEnabled,
      ...(coverageSupported.value
        ? { allowUnregisteredDoctor: local.allowUnregisteredDoctor }
        : {}),
      ...(releaseFutureUnregistered ? { releaseFutureUnregistered } : {}),
      // Flag off: payload idêntico ao de antes (sem a chave `agendaType`).
      ...(flags.enabledDashboardDemand && { agendaType: local.agendaType })
    })

    originalSnapshot.value = getLocalSnapshot()
    originalAllowUnregistered.value = local.allowUnregisteredDoctor
    toast.success('Informações básicas salvas com sucesso.')
  } catch (err: unknown) {
    // `Network.schedule.update` normaliza o erro em ServiceError, então
    // `axios.isAxiosError` deixou de casar e a mensagem do backend se perdia.
    notifyError(err, 'Não foi possível salvar as informações básicas da escala.')
  } finally {
    emit('update:isSaving', false)
  }
}

defineExpose({
  handleSave,
  hasUnsavedChanges
})
</script>
